import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { convertToRaw } from "draft-js";
import { useEffect, useMemo, useState } from "react";
import AutoCompleteUIDTO, { ALL_AUTOCOMPLETE, EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import KnowledgeBase from "../models/KnowledgeBase";
import KnowledgeBaseService from "../services/KnowledgeBaseService";
import { QueryKeyFactory } from "../services/queryKeys";
import { rethrowWithMessage, unwrap } from "../utils/queryFn";
import RoleUtils from "../utils/RoleUtils";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import { useExclusiveUrlFilters } from "./useExclusiveUrlFilters";

const knowledgeKeys = new QueryKeyFactory(KnowledgeBaseService.path);
const NO_OPTIONS: AutoCompleteUIDTO[] = [];
const DELETE_ERROR = "Error trying to delete a Bad Smell";
const FILTER_MODE_ALL = "all";
const FILTER_MODE_CATEGORY = "category";
const FILTER_MODE_CODE = "code";
const URL_FILTER_KEYS = [FILTER_MODE_CATEGORY, FILTER_MODE_CODE] as const;

function toDeleteError(error: unknown): never {
  return rethrowWithMessage(error, DELETE_ERROR);
}

function resolveFilterMode(searchByAll: boolean, searchByCategory: boolean, searchByCode: boolean): string {
  if (searchByAll || (!searchByCategory && !searchByCode)) {
    return FILTER_MODE_ALL;
  }
  if (searchByCategory) {
    return FILTER_MODE_CATEGORY;
  }
  return FILTER_MODE_CODE;
}

const useKnowledgeBaseList = () => {
  const queryClient = useQueryClient();
  const [storedKnowledgeBaseCategory, setStoredKnowledgeBaseCategory] = useLocalStorage<AutoCompleteUIDTO>(
    STORAGE_KEYS.STORED_KNOWLEDGE_BASE_CATEGORY,
    EMPTY_AUTOCOMPLETE,
  );
  const [storedKnowledgeBaseCode, setStoredKnowledgeBaseCode] = useLocalStorage<AutoCompleteUIDTO>(
    STORAGE_KEYS.STORED_KNOWLEDGE_BASE_CODE,
    EMPTY_AUTOCOMPLETE,
  );
  const [searchCategory, setSearchCategory] = useState<AutoCompleteUIDTO>(
    storedKnowledgeBaseCategory.externalId ? storedKnowledgeBaseCategory : ALL_AUTOCOMPLETE,
  );
  const [searchCode, setSearchCode] = useState<AutoCompleteUIDTO>(
    storedKnowledgeBaseCode.externalId ? storedKnowledgeBaseCode : ALL_AUTOCOMPLETE,
  );
  const searchAll = ALL_AUTOCOMPLETE;
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | undefined>(undefined);
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [searchByCategory, setSearchByCategory] = useState<boolean>(false);
  const [searchByCode, setSearchByCode] = useState<boolean>(false);
  const [searchByAll, setSearchByAll] = useState<boolean>(false);
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [contentLink, setContentLink] = useState<string | undefined>(undefined);
  const [onEditContentLink, setOnEditContentLink] = useState<string | undefined>(undefined);

  const { filters, setExclusiveFilter } = useExclusiveUrlFilters(URL_FILTER_KEYS);
  const authorizedRole = RoleUtils.hasSomeRoleExceptHR();
  const filterMode = resolveFilterMode(searchByAll, searchByCategory, searchByCode);
  const categoryLabel = searchCategory?.label ?? "";
  const codeLabel = searchCode?.label ?? "";

  const categoriesQuery = useQuery({
    queryKey: knowledgeKeys.detail("categories"),
    queryFn: () => unwrap(KnowledgeBaseService.findKnowledgeBaseCategories()),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const codesQuery = useQuery({
    queryKey: knowledgeKeys.detail("codes"),
    queryFn: () => unwrap(KnowledgeBaseService.findKnowledgeBaseCodes()),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const knowledgeBaseCategories = categoriesQuery.data ?? NO_OPTIONS;
  const knowledgeBaseCodes = codesQuery.data ?? NO_OPTIONS;

  const onDataFetch = useMemo(
    () =>
      createTableQuery<KnowledgeBase>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          knowledgeKeys.list({ page, pageSize, sortBy, sortDir, filterMode, categoryLabel, codeLabel }),
        queryFn: (page, pageSize) => {
          if (filterMode === FILTER_MODE_CATEGORY) {
            return unwrap(KnowledgeBaseService.findByCategory(page, pageSize, categoryLabel));
          }
          if (filterMode === FILTER_MODE_CODE) {
            return unwrap(KnowledgeBaseService.findByCode(page, pageSize, codeLabel));
          }
          return unwrap(KnowledgeBaseService.getAll(page, pageSize));
        },
        client: queryClient,
      }),
    [queryClient, filterMode, categoryLabel, codeLabel],
  );

  const refreshTable = () => refreshTableQuery(knowledgeKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const deleteMutation = useMutation({
    mutationFn: async (knowledgeBase: KnowledgeBase) => {
      try {
        return await unwrap(KnowledgeBaseService.delete(knowledgeBase));
      } catch (error) {
        toDeleteError(error);
      }
    },
    onSuccess: async () => {
      setSelectedKnowledgeBase(undefined);
      await refreshTable();
    },
  });

  const createMutation = useMutation({
    mutationFn: (knowledgeBase: KnowledgeBase) => unwrap(KnowledgeBaseService.create(knowledgeBase)),
    onSuccess: () => refreshTable(),
  });

  const updateMutation = useMutation({
    mutationFn: (knowledgeBase: KnowledgeBase) => unwrap(KnowledgeBaseService.update(knowledgeBase)),
    onSuccess: async () => {
      setOnEditContentLink(undefined);
      setContentLink(undefined);
      await refreshTable();
    },
  });

  useEffect(() => {
    if (filters.category) {
      const categoryFilter = knowledgeBaseCategories.find((item) => item.externalId === filters.category);
      if (categoryFilter) {
        setStoredKnowledgeBaseCategory(categoryFilter);
        setSearchCategory(categoryFilter);
      }
      setSearchByCategory(true);
      setSearchByCode(false);
      setSearchByAll(false);
      setExclusiveFilter(FILTER_MODE_CATEGORY, filters.category);
      return;
    }

    if (filters.code) {
      const codeFilter = knowledgeBaseCodes.find((item) => item.externalId === filters.code);
      // A saved entry id is not a code. Drop it once the catalog has loaded, otherwise the list goes empty.
      if (!codeFilter) {
        if (codesQuery.isSuccess) {
          setExclusiveFilter(FILTER_MODE_CODE, "");
        }
        return;
      }
      setStoredKnowledgeBaseCode(codeFilter);
      setSearchCode(codeFilter);
      setSearchByCode(true);
      setSearchByCategory(false);
      setSearchByAll(false);
      setExclusiveFilter(FILTER_MODE_CODE, filters.code);
      return;
    }

    if (storedKnowledgeBaseCategory.externalId) {
      setSearchByCategory(true);
      setSearchByCode(false);
      setSearchByAll(false);
      setExclusiveFilter(FILTER_MODE_CATEGORY, storedKnowledgeBaseCategory.externalId);
      return;
    }

    if (storedKnowledgeBaseCode.externalId) {
      const codeFilter = knowledgeBaseCodes.find((item) => item.externalId === storedKnowledgeBaseCode.externalId);
      if (!codeFilter) {
        if (codesQuery.isSuccess) {
          setStoredKnowledgeBaseCode(EMPTY_AUTOCOMPLETE);
        }
        return;
      }
      setSearchCode(codeFilter);
      setSearchByCode(true);
      setSearchByCategory(false);
      setSearchByAll(false);
      setExclusiveFilter(FILTER_MODE_CODE, codeFilter.externalId);
    }
    // useLocalStorage setters are recreated every render, so they cannot be dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.category,
    filters.code,
    knowledgeBaseCategories,
    knowledgeBaseCodes,
    codesQuery.isSuccess,
    setExclusiveFilter,
    storedKnowledgeBaseCategory.externalId,
    storedKnowledgeBaseCode.externalId,
  ]);

  const handleOnDeleteKnowledgeBase = () => {
    if (!selectedKnowledgeBase) return;
    deleteMutation.mutate(selectedKnowledgeBase);
  };

  const saveContentLink = (event: any) => {
    const rteContent = convertToRaw(event.getCurrentContent());
    rteContent && setContentLink(JSON.stringify(rteContent));
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const onCreate = authorizedRole
    ? (knowledgeBase: KnowledgeBase) => {
        if (contentLink !== undefined) {
          knowledgeBase.contentLink = contentLink;
        }
        return createMutation.mutateAsync(knowledgeBase).then(() => undefined);
      }
    : undefined;

  const onUpdate = authorizedRole
    ? (knowledgeBase: KnowledgeBase) => {
        if (contentLink !== undefined) {
          knowledgeBase.contentLink = contentLink;
        }
        return updateMutation.mutateAsync(knowledgeBase).then(() => undefined);
      }
    : undefined;

  const onEdit = authorizedRole ? (knowledgeBase: KnowledgeBase) => setOnEditContentLink(knowledgeBase.contentLink) : undefined;

  const onCancel = authorizedRole
    ? () => {
        setOnEditContentLink(undefined);
        setContentLink(undefined);
      }
    : undefined;

  const isAllOption = (value?: AutoCompleteUIDTO) => !value?.externalId || value.externalId === ALL_AUTOCOMPLETE.externalId;

  type ExclusiveFilter = {
    mode: (typeof URL_FILTER_KEYS)[number];
    setSearch: (value: AutoCompleteUIDTO) => void;
    setStored: (value: AutoCompleteUIDTO) => void;
    setActive: (value: boolean) => void;
  };

  const categoryFilter: ExclusiveFilter = {
    mode: FILTER_MODE_CATEGORY,
    setSearch: setSearchCategory,
    setStored: setStoredKnowledgeBaseCategory,
    setActive: setSearchByCategory,
  };
  const codeFilter: ExclusiveFilter = {
    mode: FILTER_MODE_CODE,
    setSearch: setSearchCode,
    setStored: setStoredKnowledgeBaseCode,
    setActive: setSearchByCode,
  };

  const resetFilter = (filter: ExclusiveFilter) => {
    filter.setSearch(ALL_AUTOCOMPLETE);
    filter.setStored(EMPTY_AUTOCOMPLETE);
    filter.setActive(false);
  };

  const onChangeFilter = (filter: ExclusiveFilter, other: ExclusiveFilter, newValue: AutoCompleteUIDTO) => {
    const isAll = isAllOption(newValue);
    resetFilter(other);
    filter.setSearch(isAll ? ALL_AUTOCOMPLETE : newValue);
    filter.setStored(isAll ? EMPTY_AUTOCOMPLETE : newValue);
    filter.setActive(!isAll);
    setSearchByAll(isAll);
    setExclusiveFilter(filter.mode, isAll ? "" : newValue.externalId);
  };

  const onChangeCategory = (newValue: AutoCompleteUIDTO) => onChangeFilter(categoryFilter, codeFilter, newValue);
  const onChangeCode = (newValue: AutoCompleteUIDTO) => onChangeFilter(codeFilter, categoryFilter, newValue);

  return {
    searchAll,
    knowledgeBaseCategories,
    searchCategory,
    onChangeCategory,
    searchCode,
    onChangeCode,
    onDataFetch,
    forceUpdateOn,
    onCreate,
    onUpdate,
    onEdit,
    onCancel,
    hideActions: !authorizedRole,
    onEditContentLink,
    setOpenDetails,
    setSelectedKnowledgeBase,
    setOpenDelete,
    openDelete,
    selectedKnowledgeBase,
    handleOnDeleteKnowledgeBase,
    handleCloseDetails,
    knowledgeBaseCodes,
    openDetails,
    saveContentLink,
    authorizedRole,
  };
};

export default useKnowledgeBaseList;
